// src/Medicalcase/components/GoLiveRoom/GoLiveRoom.jsx
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  LiveKitRoom,
  LayoutContextProvider,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  ControlBar,
  useTracks,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import {
  cancelLive,
  fetchJoinRequests,
  approveJoinRequest,
  rejectJoinRequest,
} from "../../redux/thunks/medicalCasesThunks";
import { useLiveSession } from "../../hooks/useLiveSession";
import { useLiveComments } from "../../hooks/useLiveComments";
import { getLiveCommentAuthorName } from "../../utils/liveCommentDisplay";
import { getSpecialtyLabel } from "../Specialtyfilter/specialtyLabel";
// ↑ Adaptez ce chemin si votre GoLiveRoom n'est pas dans components/GoLiveRoom/
import "./GoLiveRoom.css";

function getDoctorName(doctor, t) {
  return (
    doctor?.fullName ||
    doctor?.name ||
    doctor?.displayName ||
    t("liveRoom.unknownDoctor", { defaultValue: "Dr. Unknown" })
  );
}

function getInitials(name) {
  return (
    String(name || "").split(/\s+/).filter(Boolean).slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "").join("") || "DR"
  );
}

function isUuid(value) {
  return typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function normalizeStreamStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (["started", "live", "active", "ongoing"].includes(normalized)) return "started";
  if (["scheduled", "pending", "idle", "created"].includes(normalized)) return "scheduled";
  if (["ended", "finished", "closed", "cancelled", "canceled"].includes(normalized)) return "ended";
  return "idle";
}

// Message d'erreur : on garde le message renvoyé par l'API s'il existe,
// sinon on utilise le texte traduit de secours.
function getErrorMessage(error, fallback) {
  return typeof error === "string" ? error : error?.message || fallback;
}

function HostStage() {
  const allTracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare], {
    onlySubscribed: false,
  });
  // A watcher who hasn't been approved to publish has nothing to show — don't
  // clutter the host's grid with an empty placeholder tile for every viewer.
  const tracks = allTracks.filter(
    (trackRef) =>
      trackRef.participant.isLocal ||
      trackRef.participant.permissions?.canPublish ||
      trackRef.publication
  );

  return (
    <div className="mcd-live-room__livekit-stage">
      <LayoutContextProvider>
        <GridLayout tracks={tracks}>
          <ParticipantTile />
        </GridLayout>
        <RoomAudioRenderer />
        <ControlBar
          controls={{ camera: true, microphone: true, screenShare: true, chat: true, leave: true }}
        />
      </LayoutContextProvider>
    </div>
  );
}

// Statut technique (clé interne) -> clé de traduction
const CONNECTION_KEYS = {
  idle: "idle",
  joining: "joining",
  ready: "ready",
  "sdk-connected": "sdkConnected",
  "backend-connected": "backendConnected",
  leaving: "leaving",
  disconnected: "disconnected",
  failed: "failed",
};

const CONNECTION_DEFAULTS = {
  idle: "Not connected",
  joining: "Requesting access…",
  ready: "Connecting to media…",
  sdkConnected: "Media connected — awaiting server confirmation…",
  backendConnected: "Connected",
  leaving: "Leaving…",
  disconnected: "Disconnected",
  failed: "Connection failed",
};

function connectionLabel(status, t) {
  const key = CONNECTION_KEYS[status] || "idle";
  return t(`liveRoom.connection.${key}`, { defaultValue: CONNECTION_DEFAULTS[key] });
}

export default function GoLiveRoom({ streamData, doctor, onEnd }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const streamId = streamData?.id;
  const streamStatus = normalizeStreamStatus(streamData?.status || "scheduled");
  const [liveStarted, setLiveStarted] = useState(streamStatus === "started");
  const [credentials, setCredentials] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const isValidLiveId = isUuid(streamId);
  const doctorName = getDoctorName(doctor, t);
  const doctorInitials = useMemo(() => getInitials(doctorName), [doctorName]);
  // Valeur brute (anglais) conservée, on traduit uniquement pour l'affichage.
  const specialty = streamData?.specialty || "General";
  const specialtyLabel = getSpecialtyLabel(t, specialty);

  const {
    connection,
    connectedParticipants,
    startAndJoinOnce,
    joinOnce,
    leaveOnce,
    closeWithoutLeave,
    endOnce,
    markSdkConnected,
    markSdkError,
  } = useLiveSession(streamId, doctor);
  const backendConnected = connection?.status === "backend-connected";
  const { comments, error: commentsError, sendComment } = useLiveComments(streamId, backendConnected);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [requestActionId, setRequestActionId] = useState(null);

  React.useEffect(() => {
    if (!backendConnected || !isValidLiveId) return undefined;

    let cancelled = false;
    const refresh = () => {
      dispatch(fetchJoinRequests({ liveId: streamId }))
        .unwrap()
        .then((result) => {
          if (cancelled) return;
          const items = result?.payload?.items || [];
          setPendingRequests(items.filter((item) => item.status === "pending"));
        })
        .catch(() => {});
    };
    refresh();
    const timer = window.setInterval(refresh, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [backendConnected, dispatch, isValidLiveId, streamId]);

  const handleReviewRequest = async (requestId, approve) => {
    setRequestActionId(requestId);
    try {
      const action = approve ? approveJoinRequest : rejectJoinRequest;
      await dispatch(action({ liveId: streamId, requestId })).unwrap();
      setPendingRequests((prev) => prev.filter((item) => item.id !== requestId));
    } catch (error) {
      setActionError(
        getErrorMessage(error, t("liveRoom.errors.reviewRequest", { defaultValue: "Unable to review the request" }))
      );
    } finally {
      setRequestActionId(null);
    }
  };

  const connectWithCredentials = (result) => {
    setCredentials({ serverUrl: result.serverUrl, participantToken: result.participantToken });
  };

  const handleStart = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const result = await startAndJoinOnce();
      setLiveStarted(true);
      connectWithCredentials(result);
    } catch (error) {
      setActionError(
        getErrorMessage(error, t("liveRoom.errors.startLive", { defaultValue: "Unable to start the live" }))
      );
    } finally {
      setActionLoading(false);
    }
  };

  React.useEffect(() => {
    if (streamStatus !== "started" || credentials || actionLoading || !isValidLiveId) return;

    let cancelled = false;
    setLiveStarted(true);
    setActionLoading(true);
    joinOnce()
      .then((result) => {
        if (!cancelled) connectWithCredentials(result);
      })
      .catch((error) => {
        if (!cancelled) {
          setActionError(
            getErrorMessage(error, t("liveRoom.errors.reconnectLive", { defaultValue: "Unable to reconnect to the live" }))
          );
        }
      })
      .finally(() => {
        if (!cancelled) setActionLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionLoading, credentials, isValidLiveId, joinOnce, streamStatus]);

  const handleLeave = async () => {
    setActionLoading(true);
    setActionError(null);
    await leaveOnce();
    setCredentials(null);
    setActionLoading(false);
  };

  const handleEnd = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await endOnce();
      closeWithoutLeave();
      setCredentials(null);
      if (typeof onEnd === "function") onEnd();
    } catch (error) {
      setActionError(
        getErrorMessage(error, t("liveRoom.errors.endLive", { defaultValue: "Unable to end the live" }))
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await dispatch(cancelLive(streamId)).unwrap();
      closeWithoutLeave();
      if (typeof onEnd === "function") onEnd();
    } catch (error) {
      setActionError(
        getErrorMessage(error, t("liveRoom.errors.cancelLive", { defaultValue: "Unable to cancel the live" }))
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleSend = async (event) => {
    event.preventDefault();
    const content = chatInput.trim();
    if (!content || !backendConnected) return;
    try {
      await sendComment(content);
      setChatInput("");
    } catch (error) {
      setActionError(
        getErrorMessage(error, t("liveRoom.errors.sendComment", { defaultValue: "Unable to send the comment" }))
      );
    }
  };

  return (
    <section
      className="mcd-live-room"
      aria-label={streamData?.title || t("liveRoom.ariaLabel", { defaultValue: "Live Stream" })}
    >
      <div className="mcd-live-room__layout">
        <article className="mcd-live-room__stage">
          <div className="mcd-live-room__video">
            <div className="mcd-live-room__topbar">
              <span className="mcd-live-room__pill-live">
                {t("liveRoom.liveBadge", { defaultValue: "LIVE" })}
              </span>
              <span className="mcd-live-room__watching">
                {t("liveRoom.connectedCount", {
                  number: connectedParticipants.length,
                  defaultValue: "{{number}} connected",
                })}
              </span>
            </div>
            {credentials ? (
              <LiveKitRoom
                serverUrl={credentials.serverUrl}
                token={credentials.participantToken}
                connect
                audio
                video
                onConnected={markSdkConnected}
                onDisconnected={() => { setCredentials(null); void leaveOnce(); }}
                onError={(error) => {
                  markSdkError();
                  setActionError(
                    error?.message || t("liveRoom.errors.livekitConnect", { defaultValue: "Unable to connect to LiveKit" })
                  );
                }}
              >
                <HostStage />
              </LiveKitRoom>
            ) : (
              <>
                <div className="mcd-live-room__doctor-card">
                  <span className="mcd-live-room__avatar">{doctorInitials}</span>
                  <div>
                    <p className="mcd-live-room__doctor-name">{doctorName}</p>
                    <p className="mcd-live-room__doctor-specialty">{specialtyLabel}</p>
                  </div>
                </div>
                <div className="mcd-live-room__title-wrap">
                  <h3>{streamData?.title}</h3><p>{streamData?.description}</p>
                </div>
              </>
            )}
          </div>

          <p className="mcd-live-room__connection-status" aria-live="polite">
            {connectionLabel(connection?.status, t)}
          </p>
          {actionError ? <p className="mcd-live-room__action-error" role="alert">{actionError}</p> : null}

          <div className="mcd-live-room__controls">
            <div className="mcd-live-room__action-group">
              {!credentials && !liveStarted && streamStatus !== "ended" ? (
                <button type="button" className="mcd-live-room__start-btn"
                  disabled={!isValidLiveId || actionLoading} onClick={handleStart}>
                  {t("liveRoom.startStream", { defaultValue: "Start Stream" })}
                </button>
              ) : null}
              {liveStarted || streamStatus === "started" ? (
                <button type="button" className="mcd-live-room__end-btn" disabled={actionLoading} onClick={handleEnd}>
                  {t("liveRoom.endStream", { defaultValue: "End stream" })}
                </button>
              ) : (
                <button type="button" className="mcd-live-room__cancel-btn" disabled={actionLoading} onClick={handleCancel}>
                  {t("liveRoom.cancelStream", { defaultValue: "Cancel stream" })}
                </button>
              )}
            </div>
          </div>

        </article>

        <aside className="mcd-live-room__chat">
          {pendingRequests.length > 0 ? (
            <div className="mcd-live-room__join-requests">
              <h4>
                {t("liveRoom.joinRequestsTitle", {
                  number: pendingRequests.length,
                  defaultValue: "Requests to join ({{number}})",
                })}
              </h4>
              {pendingRequests.map((request) => (
                <div key={request.id} className="mcd-live-room__join-request-row">
                  <span>{request.fullName || request.requesterDoctorId}</span>
                  <div>
                    <button
                      type="button"
                      disabled={requestActionId === request.id}
                      onClick={() => handleReviewRequest(request.id, true)}
                    >
                      {t("liveRoom.approve", { defaultValue: "Approve" })}
                    </button>
                    <button
                      type="button"
                      disabled={requestActionId === request.id}
                      onClick={() => handleReviewRequest(request.id, false)}
                    >
                      {t("liveRoom.reject", { defaultValue: "Reject" })}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          <header className="mcd-live-room__chat-header">
            <h4>{t("liveRoom.liveChat", { defaultValue: "Live Chat" })}</h4>
          </header>
          <div className="mcd-live-room__chat-body">
            {comments.map((comment) => (
              <p key={comment.id} className="mcd-live-room__chat-line">
                <strong>{getLiveCommentAuthorName(comment, doctor)}: </strong>{comment.content}
              </p>
            ))}
            {commentsError ? <p role="alert">{commentsError}</p> : null}
          </div>
          <form className="mcd-live-room__chat-input" onSubmit={handleSend}>
            <input
              type="text"
              value={chatInput}
              onChange={(event) => setChatInput(event.target.value)}
              placeholder={
                backendConnected
                  ? t("liveRoom.sendMessagePlaceholder", { defaultValue: "Send a message…" })
                  : t("liveRoom.chatUnavailable", { defaultValue: "Chat available after connection confirmation" })
              }
              aria-label={t("liveRoom.sendMessagePlaceholder", { defaultValue: "Send a message…" })}
              disabled={!backendConnected}
            />
            <button type="submit" disabled={!backendConnected || !chatInput.trim()}>
              {t("liveRoom.send", { defaultValue: "Send" })}
            </button>
          </form>
        </aside>
      </div>
    </section>
  );
}